import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { api, apiError } from "../../lib/api";
import { Btn, Empty } from "../../components/UI";
import { SlidePanel, ConfirmDialog } from "../../components/SlidePanel";
import { FLabel, FInput, FSelect, FTextarea } from "../engagement/FormControls";
import { DateField } from "../engagement/DateField";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const fmt = (d) => (d ? dayjs(d).format("MM/DD/YYYY") : "-");
const chip = (color) => `rounded-full px-2 py-0.5 text-[10px] font-bold ${color}`;
const STATUS_TONE = {
  Pending: "bg-[#FFF3E0] text-[#8D5A00]",
  Scheduled: "bg-[#E3F2FD] text-[#1565C0]",
  Attended: "bg-[#E8F5E9] text-[#2E7D32]",
  "No-Show": "bg-[#FFEBEE] text-[#C62828]",
  Cancelled: "bg-[#EFEBE0] text-[#6D4C41]",
};
const PRIO_TONE = {
  Routine: "bg-[#EFEBE0] text-[#6D4C41]",
  Urgent: "bg-[#FFF3E0] text-[#8D5A00]",
  STAT: "bg-[#FFEBEE] text-[#C62828]",
};

const EMPTY = { referral_date: "", referred_to_provider_name: "", referred_to_agency: "",
                referred_to_contact: "", referred_to_phone: "", referred_to_email: "",
                referral_type: "Specialist Consult", urgency: "Routine",
                authorization_number: "", insurance_carrier: "",
                reason: "", priority: "Routine", status: "Pending",
                appointment_scheduled: false, schedule_date: "", schedule_time: "",
                client_aware: false, instruction: "", additional_information: "",
                report_received_date: "", report_summary: "", followup_needed: false, notes: "" };

const REFERRAL_TYPES = ["Specialist Consult", "Imaging", "Diagnostic Testing", "Therapy",
                         "Surgery", "Inpatient Admission", "Home Health", "DME (Durable Medical Equipment)",
                         "Second Opinion", "Genetic Counseling"];
const URGENCIES = ["Routine", "Urgent", "Emergent", "STAT"];

export default function MedicalReferralList({ client, editable, dict }) {
  const statuses = dict?.referral_statuses || ["Pending", "Scheduled", "Attended", "No-Show", "Cancelled"];
  const priorities = dict?.referral_priorities || ["Routine", "Urgent", "STAT"];
  const [items, setItems] = useState([]);
  const [panel, setPanel] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [del, setDel] = useState(null);
  const [q, setQ] = useState("");
  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const load = () => api.get(`/clients/${client.id}/medical-referrals`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [client.id]); // eslint-disable-line
  const filtered = useMemo(() => items.filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [r.referred_to_provider_name, r.referred_to_agency, r.reason, r.status, r.priority].some((v) => (v || "").toLowerCase().includes(s));
  }), [items, q]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.referred_to_provider_name.trim()) return toast.error("Referred to provider is required");
    if (!form.reason.trim()) return toast.error("Reason is required");
    try {
      if (editId) await api.put(`/medical-referrals/${editId}`, form);
      else await api.post(`/clients/${client.id}/medical-referrals`, form);
      toast.success(editId ? "Referral updated" : "Referral added");
      setPanel(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const remove = async () => {
    try { await api.delete(`/medical-referrals/${del.id}`); toast.success("Referral removed"); setDel(null); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="referrals-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-heading text-lg font-bold text-[#3E2723]">All Medical Referrals</h3>
          <p className="text-xs text-[#7B6157]">{filtered.length} entr{filtered.length === 1 ? "y" : "ies"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input data-testid="referral-filter" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)}
            className="rounded-full border border-[#E5DFD3] bg-white px-3.5 py-1.5 text-xs outline-none w-44 focus:border-[#8D6E63]" />
          {editable && <Btn data-testid="add-referral-btn" variant="outline"
            onClick={() => { setForm({ ...EMPTY, referral_date: dayjs().format("YYYY-MM-DD") }); setEditId(null); setPanel(true); }}>
            <Plus size={14} /> Add New Medical Referrals</Btn>}
        </div>
      </div>

      {filtered.length === 0 ? <Empty text="No referrals on file" /> : (
        <div className="overflow-x-auto rounded-2xl border border-[#EFEBE0]">
          <table className="min-w-full text-sm">
            <thead className="bg-[#FDFCF8]">
              <tr className="text-left text-[10px] font-bold uppercase text-[#7B6157]">
                {["Date","Referred To","Agency","Reason","Priority","Status","Scheduled?","Schedule Date","Time","Client Aware?","Instruction","Actions"].map((h) => (
                  <th key={h} className={`px-3 py-3 whitespace-nowrap ${h === "Actions" ? "text-right" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`referral-row-${r.id}`} className="border-t border-[#EFEBE0]">
                  <td className="px-3 py-2 text-[#7B6157] whitespace-nowrap">{fmt(r.referral_date)}</td>
                  <td className="px-3 py-2 font-semibold text-[#3E2723] whitespace-nowrap">{r.referred_to_provider_name}</td>
                  <td className="px-3 py-2 text-[#7B6157]">{r.referred_to_agency || "-"}</td>
                  <td className="px-3 py-2 text-[#7B6157] max-w-[220px] truncate" title={r.reason}>{r.reason || "-"}</td>
                  <td className="px-3 py-2"><span className={chip(PRIO_TONE[r.priority] || PRIO_TONE.Routine)}>{r.priority}</span></td>
                  <td className="px-3 py-2"><span className={chip(STATUS_TONE[r.status] || STATUS_TONE.Pending)}>{r.status}</span></td>
                  <td className="px-3 py-2 text-[#7B6157]">{r.appointment_scheduled ? "Yes" : "No"}</td>
                  <td className="px-3 py-2 text-[#7B6157] whitespace-nowrap">{fmt(r.schedule_date)}</td>
                  <td className="px-3 py-2 text-[#7B6157]">{r.schedule_time || "-"}</td>
                  <td className="px-3 py-2 text-[#7B6157]">{r.client_aware ? "Yes" : "No"}</td>
                  <td className="px-3 py-2 text-[#7B6157] max-w-[180px] truncate" title={r.instruction}>{r.instruction || "-"}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      {editable && (
                        <>
                          <button data-testid={`edit-referral-${r.id}`} onClick={() => { setForm({ ...EMPTY, ...r }); setEditId(r.id); setPanel(true); }}
                            className="h-8 w-8 rounded-full flex items-center justify-center text-[#7B6157] hover:bg-[#EDE7DC]"><Pencil size={13} /></button>
                          <button data-testid={`delete-referral-${r.id}`} onClick={() => setDel(r)}
                            className="h-8 w-8 rounded-full flex items-center justify-center text-[#C62828] hover:bg-[#FFEBEE]"><Trash2 size={13} /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SlidePanel open={panel} onClose={() => setPanel(false)} title={editId ? "Edit Medical Referral" : "Add New Medical Referral"} testId="referral-slide-panel">
        <form onSubmit={submit} className="space-y-4 p-1">
          <div><FLabel>Referral Date</FLabel><DateField testId="referral-date" value={form.referral_date} onChange={(v) => setForm((s) => ({ ...s, referral_date: v }))} /></div>
          <div className="rounded-xl border border-[#EFEBE0] p-3 bg-[#FDFCF8] space-y-3">
            <div className="text-[10px] font-bold uppercase text-[#7B6157]">Referred To Provider</div>
            <div className="grid grid-cols-2 gap-3">
              <div><FLabel>Provider Name *</FLabel><FInput data-testid="referral-provider" value={form.referred_to_provider_name} onChange={f("referred_to_provider_name")} placeholder="Dr Silva" /></div>
              <div><FLabel>Agency</FLabel><FInput data-testid="referral-agency" value={form.referred_to_agency} onChange={f("referred_to_agency")} placeholder="Cocoa Cardiology" /></div>
              <div><FLabel>Contact Person</FLabel><FInput data-testid="referral-contact" value={form.referred_to_contact} onChange={f("referred_to_contact")} /></div>
              <div><FLabel>Phone</FLabel><FInput data-testid="referral-phone" value={form.referred_to_phone} onChange={f("referred_to_phone")} /></div>
              <div className="col-span-2"><FLabel>Email</FLabel><FInput data-testid="referral-email" value={form.referred_to_email} onChange={f("referred_to_email")} /></div>
            </div>
          </div>
          <div><FLabel>Reason for Referral *</FLabel><FTextarea data-testid="referral-reason" rows={2} value={form.reason} onChange={f("reason")} placeholder="Baseline EKG before starting antipsychotic" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><FLabel>Priority</FLabel><FSelect data-testid="referral-priority" value={form.priority} onChange={f("priority")}>{priorities.map((p) => <option key={p}>{p}</option>)}</FSelect></div>
            <div><FLabel>Status</FLabel><FSelect data-testid="referral-status" value={form.status} onChange={f("status")}>{statuses.map((s) => <option key={s}>{s}</option>)}</FSelect></div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-[#5D4037]">
            <input type="checkbox" data-testid="referral-scheduled" checked={!!form.appointment_scheduled}
              onChange={(e) => setForm((s) => ({ ...s, appointment_scheduled: e.target.checked }))} />
            Has appointment been scheduled?
          </label>
          {form.appointment_scheduled && (
            <div className="grid grid-cols-2 gap-3">
              <div><FLabel>Schedule Date</FLabel><DateField testId="referral-schedule-date" value={form.schedule_date} onChange={(v) => setForm((s) => ({ ...s, schedule_date: v }))} /></div>
              <div><FLabel>Time</FLabel><FInput data-testid="referral-schedule-time" value={form.schedule_time} onChange={f("schedule_time")} placeholder="10:00 AM" /></div>
            </div>
          )}
          <label className="inline-flex items-center gap-2 text-sm text-[#5D4037]">
            <input type="checkbox" data-testid="referral-client-aware" checked={!!form.client_aware}
              onChange={(e) => setForm((s) => ({ ...s, client_aware: e.target.checked }))} />
            Client is aware of the appointment
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div><FLabel>Referral Type</FLabel><FSelect data-testid="referral-type" value={form.referral_type} onChange={f("referral_type")}>
              {REFERRAL_TYPES.map((r) => <option key={r}>{r}</option>)}
            </FSelect></div>
            <div><FLabel>Urgency</FLabel><FSelect data-testid="referral-urgency" value={form.urgency} onChange={f("urgency")}>
              {URGENCIES.map((u) => <option key={u}>{u}</option>)}
            </FSelect></div>
            <div><FLabel>Insurance Carrier</FLabel><FInput data-testid="referral-insurance" value={form.insurance_carrier} onChange={f("insurance_carrier")} placeholder="e.g., BCBS NC" /></div>
            <div><FLabel>Authorization Number</FLabel><FInput data-testid="referral-auth" value={form.authorization_number} onChange={f("authorization_number")} placeholder="Prior-auth #" /></div>
          </div>
          <div><FLabel>Instruction</FLabel><FTextarea data-testid="referral-instruction" rows={2} value={form.instruction} onChange={f("instruction")} /></div>
          <div><FLabel>Additional Information</FLabel><FTextarea data-testid="referral-additional" rows={2} value={form.additional_information} onChange={f("additional_information")} /></div>
          <div className="rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] p-3 space-y-3">
            <div className="text-[10px] font-bold uppercase text-[#7B6157]">Referral Outcome</div>
            <div className="grid grid-cols-2 gap-3">
              <div><FLabel>Report Received Date</FLabel><DateField testId="referral-report-received" value={form.report_received_date} onChange={(v) => setForm((s) => ({ ...s, report_received_date: v }))} /></div>
              <div className="flex items-end gap-2">
                <label className="inline-flex items-center gap-2 text-sm text-[#5D4037]">
                  <input type="checkbox" data-testid="referral-followup-needed" checked={!!form.followup_needed}
                    onChange={(e) => setForm((s) => ({ ...s, followup_needed: e.target.checked }))} />
                  Follow-up needed
                </label>
              </div>
              <div className="col-span-2"><FLabel>Report Summary</FLabel><FTextarea data-testid="referral-report-summary" rows={2} value={form.report_summary} onChange={f("report_summary")} placeholder="Key findings, recommendations, next steps" /></div>
            </div>
          </div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-4">
            <Btn type="button" variant="ghost" onClick={() => setPanel(false)}>Cancel</Btn>
            <Btn data-testid="referral-save" type="submit">{editId ? "Save Changes" : "Add"}</Btn>
          </div>
        </form>
      </SlidePanel>

      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={remove}
        title="Remove referral" description={`Delete referral to ${del?.referred_to_provider_name}?`} />
    </div>
  );
}
